import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { AlertTriangle, ShoppingCart } from 'lucide-react'
import type { Product } from '#bv/data/products'
import { EliteBadge } from '#bv/components/EliteBadge'
import { addToCart, setCartItems, type CartItem } from '#bv/lib/cart'
import { DisplayPrice } from '#bv/components/DisplayPrice'
import { useCurrency } from '#bv/lib/currency/CurrencyContext'
import {
  defaultSmmTierId,
  listSmmTiers,
  resolveSmmConfigForTier,
  smmHasTiers,
} from '#bv/lib/smmTiers'
import {
  formatSmmTargetForBuyer,
  isValidSmmTarget,
  normalizeSmmTargetForProduct,
  smmMetricLabel,
  smmPlatformLabel,
  smmTargetInputForConfig,
  smmTargetValidationMessageForProduct,
} from '#bv/lib/smmTarget'
import { useProductStock } from '#bv/lib/useProductStock'

type Props = {
  product: Product & { smmConfig: NonNullable<Product['smmConfig']> }
  compact?: boolean
}

const pillBase =
  'inline-flex w-full min-h-[2.75rem] items-center justify-center gap-2 rounded-full font-semibold tracking-wide transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#00bfff]/45 disabled:cursor-not-allowed disabled:opacity-45 active:scale-[0.98]'

export function SmmProductPurchase({ product, compact }: Props) {
  const { formatUsdCharge } = useCurrency()
  const navigate = useNavigate()
  const { inStock } = useProductStock()
  const baseCfg = product.smmConfig
  const tiers = listSmmTiers(baseCfg)
  const hasTiers = smmHasTiers(baseCfg)
  const [tierId, setTierId] = useState(() => defaultSmmTierId(baseCfg))
  const activeCfg = useMemo(
    () => (hasTiers ? resolveSmmConfigForTier(baseCfg, tierId) : baseCfg),
    [baseCfg, hasTiers, tierId],
  )
  const targetUi = smmTargetInputForConfig(baseCfg)
  const packages = activeCfg.packages
  const [pkgIdx, setPkgIdx] = useState(0)
  const [targetInput, setTargetInput] = useState('')
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [pendingAction, setPendingAction] = useState<'cart' | 'buy' | null>(null)
  const [addedFlash, setAddedFlash] = useState(false)
  const [fieldError, setFieldError] = useState('')

  useEffect(() => {
    setPkgIdx(0)
  }, [tierId])

  const selected = packages[pkgIdx] ?? packages[0]
  const productInStock = inStock(product.id)
  const targetErr = targetInput.trim() ? smmTargetValidationMessageForProduct(targetInput, baseCfg) : null
  const normalizedTarget = useMemo(
    () => normalizeSmmTargetForProduct(targetInput, baseCfg),
    [targetInput, baseCfg],
  )

  const lineItem = (): CartItem => ({
    productId: product.id,
    name: `${product.name} — ${activeCfg.tierLabel} — ${selected.label}`,
    unitPrice: selected.price,
    quantity: 1,
    smmPackageQuantity: selected.quantity,
    smmTiktokTarget: normalizedTarget ?? targetInput.trim(),
    smmPlatform: baseCfg.platform,
    smmServiceId: baseCfg.serviceId,
    ...(hasTiers ? { smmTierId: tierId } : {}),
  })

  const validateBeforeCheckout = (): boolean => {
    const msg = smmTargetValidationMessageForProduct(targetInput, baseCfg)
    if (msg) {
      setFieldError(msg)
      return false
    }
    setFieldError('')
    return true
  }

  const runCheckoutAction = (action: 'cart' | 'buy') => {
    if (!productInStock) return
    if (!validateBeforeCheckout()) return
    const item = lineItem()
    if (action === 'cart') {
      addToCart(item, 1)
      setAddedFlash(true)
      window.setTimeout(() => setAddedFlash(false), 1600)
    } else {
      setCartItems([item])
      void navigate({ to: '/cart', search: { checkout: '1' } })
    }
    setConfirmOpen(false)
    setPendingAction(null)
  }

  if (compact) return null

  const pad = 'py-3 text-sm sm:min-h-[2.875rem] sm:text-[0.9375rem]'
  const metric = smmMetricLabel(baseCfg.metric)

  return (
    <div className="flex w-full flex-col gap-4 sm:gap-5">
      {hasTiers ? (
        <div>
          <p className="m-0 text-[0.62rem] font-bold uppercase tracking-[0.18em] text-[#5a6d8a]">Choose quality tier</p>
          <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-3">
            {tiers.map((t) => {
              const on = tierId === t.id
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setTierId(t.id)}
                  className={`rounded-xl border px-3 py-3 text-left transition-all ${
                    on
                      ? 'border-[rgba(0, 191, 255,0.55)] bg-[rgba(0, 191, 255,0.12)] shadow-[0_0_20px_rgba(0, 191, 255,0.12)]'
                      : 'border-white/[0.1] bg-white/[0.02] hover:border-[rgba(0, 191, 255,0.28)]'
                  }`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-bold text-[#f0f6ff]">{t.label}</span>
                    {t.id === 'elite' ? <EliteBadge size="sm" rounded="md" /> : null}
                  </div>
                  <p className="m-0 mt-1.5 text-[0.65rem] leading-snug text-[#5a6d8a]">{t.blurb}</p>
                  <p className="m-0 mt-2 text-[0.6rem] font-semibold uppercase tracking-wide text-[#ff8fd4]">
                    Drop: {t.dropRate}
                    {t.refillDays > 0 ? ` · ${t.refillDays}d refill` : ' · no refill'}
                  </p>
                </button>
              )
            })}
          </div>
        </div>
      ) : null}

      <div className="flex flex-wrap gap-1.5 sm:gap-2">
        {activeCfg.trustBadges.map((b) => (
          <span
            key={b.label}
            className="rounded-full border border-[rgba(0,191,255,0.22)] bg-[rgba(0, 191, 255,0.08)] px-2 py-0.5 text-[0.58rem] font-bold uppercase tracking-wide text-[#d4e8ff] sm:px-2.5 sm:py-1 sm:text-[0.62rem]"
          >
            {b.label}
          </span>
        ))}
      </div>

      <div className="grid grid-cols-2 gap-2 text-xs sm:grid-cols-3">
        <Stat label="Start time" value={activeCfg.startTime} />
        <Stat label="Speed" value={activeCfg.deliverySpeed} />
        <Stat label="Drop risk" value={activeCfg.dropRate} className="col-span-2 sm:col-span-1" />
      </div>

      <div>
        <p className="m-0 text-[0.62rem] font-bold uppercase tracking-[0.18em] text-[#5a6d8a]">Choose package</p>
        <div className="mt-2 grid grid-cols-1 gap-2 min-[400px]:grid-cols-2 sm:grid-cols-3">
          {packages.map((p, i) => (
            <button
              key={p.quantity}
              type="button"
              onClick={() => setPkgIdx(i)}
              className={`rounded-xl border px-3 py-3 text-left transition-all sm:px-2.5 sm:py-2.5 ${
                pkgIdx === i
                  ? 'border-[rgba(0, 191, 255,0.55)] bg-[rgba(0, 191, 255,0.12)]'
                  : 'border-white/[0.1] bg-white/[0.02] hover:border-[rgba(0, 191, 255,0.3)]'
              }`}
            >
              <div className="text-xs font-semibold text-[#f0f6ff] sm:text-[0.65rem]">{p.label}</div>
              <DisplayPrice usd={p.price} className="mt-1 font-display text-xl tabular-nums text-[#d4e8ff] sm:text-lg" />
            </button>
          ))}
        </div>
      </div>

      <div>
        <label htmlFor="smm-target" className="text-[0.62rem] font-bold uppercase tracking-[0.18em] text-[#5a6d8a]">
          {targetUi.label}
        </label>
        <input
          id="smm-target"
          type="text"
          value={targetInput}
          onChange={(e) => {
            setTargetInput(e.target.value)
            setFieldError('')
          }}
          placeholder={targetUi.placeholder}
          className="mt-2 w-full rounded-xl border border-white/[0.12] bg-black/35 px-3.5 py-3 text-base text-[#f0f6ff] placeholder:text-[#5a6d8a] focus:border-[rgba(0, 191, 255,0.45)] focus:outline-none focus:ring-2 focus:ring-[#00bfff]/25 sm:py-2.5 sm:text-sm"
          autoComplete="off"
          spellCheck={false}
        />
        {(fieldError || targetErr) && targetInput.trim() ? (
          <p className="mt-1.5 text-xs text-rose-300">{fieldError || targetErr}</p>
        ) : (
          <p className="mt-1.5 text-xs leading-relaxed text-[#5a6d8a]">{targetUi.hint}</p>
        )}
      </div>

      <div className="flex gap-3 rounded-xl border border-amber-500/25 bg-amber-500/[0.07] px-3.5 py-3 text-xs leading-relaxed text-amber-100/90">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-400" aria-hidden />
        <p className="m-0">{baseCfg.publicProfileWarning}</p>
      </div>

      {!productInStock ? (
        <button type="button" disabled className={`${pillBase} ${pad} border border-white/12 bg-white/[0.03] text-[#5a6d8a]`}>
          Out of stock
        </button>
      ) : (
        <div className="flex w-full flex-col gap-2">
          <button
            type="button"
            onClick={() => {
              if (validateBeforeCheckout()) {
                setPendingAction('buy')
                setConfirmOpen(true)
              }
            }}
            className={`btn-primary ${pillBase} ${pad}`}
          >
            Buy now — {formatUsdCharge(selected.price)}
          </button>
          <button
            type="button"
            onClick={() => {
              if (validateBeforeCheckout()) {
                setPendingAction('cart')
                setConfirmOpen(true)
              }
            }}
            className={`${pillBase} ${pad} border border-white/20 bg-white/[0.03] text-[#f0f6ff] ${addedFlash ? 'ring-2 ring-emerald-400/45' : ''}`}
          >
            <ShoppingCart className="h-[1.1em] w-[1.1em] shrink-0" strokeWidth={2} aria-hidden />
            {addedFlash ? 'Added' : 'Add to cart'}
          </button>
        </div>
      )}

      {confirmOpen ? (
        <div
          className="fixed inset-0 z-[200] flex items-end justify-center bg-black/70 p-0 backdrop-blur-sm sm:items-center sm:p-4"
          role="dialog"
          aria-modal="true"
        >
          <div className="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-t-2xl border border-white/[0.12] bg-[#121018] p-5 sm:rounded-2xl">
            <h3 className="font-display text-lg text-[#f0f6ff]">Confirm before checkout</h3>
            <p className="mt-3 text-sm leading-relaxed text-[#8aa0c4]">{baseCfg.publicProfileWarning}</p>
            <p className="mt-2 text-sm text-[#5a6d8a]">
              {smmPlatformLabel(baseCfg.platform)}:{' '}
              <span className="text-[#d4e8ff]">
                {isValidSmmTarget(targetInput, baseCfg.platform)
                  ? formatSmmTargetForBuyer(
                      normalizeSmmTargetForProduct(targetInput, baseCfg) ?? targetInput.trim(),
                      baseCfg.platform,
                    )
                  : targetInput.trim()}
              </span>
            </p>
            <p className="mt-1 text-xs text-[#5a6d8a]">
              {activeCfg.tierLabel} · {selected.quantity.toLocaleString()} {metric}
            </p>
            <div className="mt-5 flex flex-col gap-2 sm:flex-row">
              <button
                type="button"
                className="flex-1 rounded-full border border-white/15 py-3 text-sm font-semibold text-[#a8c4e8] sm:py-2.5"
                onClick={() => {
                  setConfirmOpen(false)
                  setPendingAction(null)
                }}
              >
                Go back
              </button>
              <button
                type="button"
                className="btn-primary flex-1 rounded-full py-3 text-sm font-semibold sm:py-2.5"
                onClick={() => pendingAction && runCheckoutAction(pendingAction)}
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  )
}

function Stat({ label, value, className }: { label: string; value: string; className?: string }) {
  return (
    <div className={`rounded-xl border border-white/[0.08] bg-black/25 px-3 py-2.5 sm:py-2 ${className ?? ''}`}>
      <div className="text-[0.58rem] font-bold uppercase tracking-wider text-[#5a6d8a]">{label}</div>
      <div className="mt-0.5 text-sm font-semibold leading-snug text-[#f0f6ff] sm:text-[0.9375rem]">{value}</div>
    </div>
  )
}
